import I18n from 'I18n';
import { LearningPathAuditEntryTypename, LearningPathFilterAuditEntryTypename, PlaylistAuditEntryTypename, PlaylistItemAuditEntryTypename } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/playlists/rpc/AuditChirpService';
export const ITEM_TYPE_LABELS = {
  TRACK: 'Course',
  LESSON: 'Lesson',
  SHORT_FORM_CONTENT: 'Video'
};
export const isPlaylistEntry = entry => entry.__typename === PlaylistAuditEntryTypename;
export const isPlaylistItemEntry = entry => entry.__typename === PlaylistItemAuditEntryTypename;
export const isLearningPathEntry = entry => entry.__typename === LearningPathAuditEntryTypename;
export const isLearningPathFilterEntry = entry => entry.__typename === LearningPathFilterAuditEntryTypename;
export const detectPlaylistChanges = (current, previous) => {
  if (!previous) return undefined;
  const changes = {};
  if (current.name !== previous.name) {
    changes.nameChanged = current.name;
  }
  if (current.description !== previous.description) {
    changes.descriptionChanged = true;
  }
  if (current.thumbnailSquareUrl !== previous.thumbnailSquareUrl) {
    changes.thumbnailChanged = true;
  }
  if (current.language !== previous.language) {
    changes.languageChanged = current.language;
  }
  return Object.keys(changes).length > 0 ? changes : undefined;
};
export const detectLearningPathChanges = (current, previous) => {
  if (!previous) return undefined;
  const changes = {};
  if (current.internalName !== previous.internalName) {
    changes.internalNameChanged = current.internalName;
  }
  if (current.visibility !== previous.visibility) {
    changes.visibilityChanged = current.visibility.toString();
  }
  if (current.active !== previous.active) {
    changes.activeChanged = current.active ? 'published' : 'unpublished';
  }
  return Object.keys(changes).length > 0 ? changes : undefined;
};
const getPlaylistItemDetails = ({
  entry
}) => {
  var _ITEM_TYPE_LABELS$ite;
  const item = entry;
  const itemType = (_ITEM_TYPE_LABELS$ite = ITEM_TYPE_LABELS[item.type.toString()]) !== null && _ITEM_TYPE_LABELS$ite !== void 0 ? _ITEM_TYPE_LABELS$ite : 'Item';
  return I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.details.itemType', {
    type: itemType,
    name: item.name
  });
};
const displayRules = [
// PlaylistEntry rules
{
  match: ({
    entry
  }) => isPlaylistEntry(entry) && entry.deletedAt > 0,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistDeleted'),
  getDetails: ({
    entry
  }) => entry.name
}, {
  match: ({
    entry,
    isFirstEntry
  }) => isPlaylistEntry(entry) && !!isFirstEntry,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistCreated'),
  getDetails: ({
    entry
  }) => entry.name
}, {
  match: ({
    entry,
    playlistChanges
  }) => isPlaylistEntry(entry) && !!(playlistChanges !== null && playlistChanges !== void 0 && playlistChanges.nameChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.nameChanged'),
  getDetails: ({
    playlistChanges
  }) => playlistChanges.nameChanged
}, {
  match: ({
    entry,
    playlistChanges
  }) => isPlaylistEntry(entry) && !!(playlistChanges !== null && playlistChanges !== void 0 && playlistChanges.descriptionChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.descriptionChanged'),
  getDetails: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.details.descriptionChanged')
}, {
  match: ({
    entry,
    playlistChanges
  }) => isPlaylistEntry(entry) && !!(playlistChanges !== null && playlistChanges !== void 0 && playlistChanges.thumbnailChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.thumbnailChanged'),
  getDetails: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.details.thumbnailChanged')
}, {
  match: ({
    entry,
    playlistChanges
  }) => isPlaylistEntry(entry) && !!(playlistChanges !== null && playlistChanges !== void 0 && playlistChanges.languageChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.languageChanged'),
  getDetails: ({
    playlistChanges
  }) => playlistChanges.languageChanged.toUpperCase()
}, {
  match: ({
    entry
  }) => isPlaylistEntry(entry),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistUpdated'),
  getDetails: ({
    entry
  }) => entry.name
},
// PlaylistItemEntry rules
{
  match: ({
    entry
  }) => isPlaylistItemEntry(entry) && entry.deletedAt > 0,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.itemRemoved'),
  getDetails: getPlaylistItemDetails
}, {
  match: ({
    entry
  }) => isPlaylistItemEntry(entry),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.itemAdded'),
  getDetails: getPlaylistItemDetails
},
// LearningPathEntry rules
{
  match: ({
    entry
  }) => isLearningPathEntry(entry) && entry.deletedAt > 0,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistDeleted'),
  getDetails: ({
    entry
  }) => entry.internalName
}, {
  match: ({
    entry,
    isFirstEntry
  }) => isLearningPathEntry(entry) && !!isFirstEntry,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.learningPathCreated'),
  getDetails: ({
    entry
  }) => entry.internalName
}, {
  match: ({
    entry,
    learningPathChanges
  }) => isLearningPathEntry(entry) && (learningPathChanges === null || learningPathChanges === void 0 ? void 0 : learningPathChanges.activeChanged) === 'published',
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistPublished'),
  getDetails: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistPublished')
}, {
  match: ({
    entry,
    learningPathChanges
  }) => isLearningPathEntry(entry) && (learningPathChanges === null || learningPathChanges === void 0 ? void 0 : learningPathChanges.activeChanged) === 'unpublished',
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistUnpublished'),
  getDetails: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.playlistUnpublished')
}, {
  match: ({
    entry,
    learningPathChanges
  }) => isLearningPathEntry(entry) && !!(learningPathChanges !== null && learningPathChanges !== void 0 && learningPathChanges.internalNameChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.internalNameChanged'),
  getDetails: ({
    learningPathChanges
  }) => learningPathChanges.internalNameChanged
}, {
  match: ({
    entry,
    learningPathChanges
  }) => isLearningPathEntry(entry) && !!(learningPathChanges !== null && learningPathChanges !== void 0 && learningPathChanges.visibilityChanged),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.visibilityChanged'),
  getDetails: ({
    learningPathChanges
  }) => learningPathChanges.visibilityChanged
}, {
  match: ({
    entry
  }) => isLearningPathEntry(entry),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.learningPathUpdated'),
  getDetails: ({
    entry
  }) => entry.internalName
},
// LearningPathFilterEntry rules
{
  match: ({
    entry
  }) => isLearningPathFilterEntry(entry) && entry.deletedAt > 0,
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.filterRemoved'),
  getDetails: ({
    entry
  }) => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.details.filterType', {
    filterType: entry.filterType
  })
}, {
  match: ({
    entry
  }) => isLearningPathFilterEntry(entry),
  getAction: () => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.actions.filterAdded'),
  getDetails: ({
    entry
  }) => I18n.text('academyCommon.playlists.hubspotPlaylists.auditLog.details.filterType', {
    filterType: entry.filterType
  })
}];
const getDisplayRule = processedEntry => displayRules.find(r => r.match(processedEntry));
export const getActionLabel = processedEntry => {
  const rule = getDisplayRule(processedEntry);
  return rule ? rule.getAction() : '';
};
export const getDetailsLabel = processedEntry => {
  const rule = getDisplayRule(processedEntry);
  return rule ? rule.getDetails(processedEntry) : '';
};
const isRedundantEntry = processedEntry => {
  const {
    entry,
    playlistChanges,
    learningPathChanges,
    isFirstEntry
  } = processedEntry;
  return isPlaylistEntry(entry) && !playlistChanges && !isFirstEntry || isLearningPathEntry(entry) && !learningPathChanges && !isFirstEntry;
};
export const processAuditEntries = entries => {
  const sortedEntries = [...entries].sort((a, b) => a.modifiedAt - b.modifiedAt);
  let previousPlaylistEntry;
  let previousLearningPathEntry;
  const processed = sortedEntries.map(entry => {
    const processedEntry = {
      entry
    };
    if (isPlaylistEntry(entry)) {
      if (!previousPlaylistEntry) {
        processedEntry.isFirstEntry = true;
      } else {
        processedEntry.playlistChanges = detectPlaylistChanges(entry, previousPlaylistEntry);
      }
      previousPlaylistEntry = entry;
    }
    if (isLearningPathEntry(entry)) {
      if (!previousLearningPathEntry) {
        processedEntry.isFirstEntry = true;
      } else {
        processedEntry.learningPathChanges = detectLearningPathChanges(entry, previousLearningPathEntry);
      }
      previousLearningPathEntry = entry;
    }
    return processedEntry;
  });
  return processed.filter(entry => !isRedundantEntry(entry));
};